import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Base service class that enforces organization-scoped queries
 * ALL data access services MUST extend this class
 */
export abstract class BaseOrganizationService<T> {
  protected supabase: SupabaseClient;
  protected organizationId: string;
  protected tableName: string;

  constructor(supabase: SupabaseClient, organizationId: string, tableName: string) {
    if (!organizationId) {
      throw new Error('Organization ID is required for all data operations');
    }
    this.supabase = supabase;
    this.organizationId = organizationId;
    this.tableName = tableName;
  }

  /**
   * Base query builder with automatic organization filter
   */
  protected baseQuery() {
    return this.supabase
      .from(this.tableName)
      .select()
      .eq('organization_id', this.organizationId);
  }

  /**
   * Get all records for this organization
   */
  async getAll(): Promise<T[]> {
    const { data, error } = await this.baseQuery();
    if (error) throw error;
    return data as T[];
  }

  /**
   * Get single record by ID (org-scoped)
   */
  async getById(id: string): Promise<T | null> {
    const { data, error } = await this.baseQuery()
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as T;
  }

  /**
   * Create record with automatic organization_id
   */
  async create(data: Partial<T>): Promise<T> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert({
        ...data,
        organization_id: this.organizationId,
      })
      .select()
      .single();
    
    if (error) throw error;
    return created as T;
  }

  /**
   * Update record (org-scoped)
   */
  async update(id: string, data: Partial<T>): Promise<T> {
    const { data: updated, error } = await this.supabase
      .from(this.tableName)
      .update(data)
      .eq('organization_id', this.organizationId)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return updated as T;
  }

  /**
   * Delete record (org-scoped)
   */
  async delete(id: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.tableName)
      .delete()
      .eq('organization_id', this.organizationId)
      .eq('id', id);
    
    if (error) throw error;
  }

  /**
   * Count records (org-scoped)
   */
  async count(): Promise<number> {
    const { count, error } = await this.supabase
      .from(this.tableName)
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', this.organizationId);
    
    if (error) throw error;
    return count || 0;
  }
}
